/**
 * swederm — Cart Discount + Gift Card Handler (Krok 2 — v6)
 * Plik: assets/cart-discount-swederm.js
 *
 * Obsługuje JEDNO pole, w które użytkownik może wpisać:
 *   • kod rabatowy (discount code) — aplikowany przez /cart/update.js
 *   • numer karty podarunkowej (gift card) — zapisywany w sessionStorage
 *     i doklejany do URL checkoutu przy kliknięciu "Do kasy"
 *
 * Rozpoznawanie typu:
 *   • Shopify gift cards mają standardowo 16 znaków alfanumerycznych
 *     (bez 0,1,O,I — ale my nie polegamy na tym, bo użytkownik mógł wpisać
 *     z myślnikami/spacjami). Po stripie separatorów: 16+ znaków
 *     alfanumerycznych → gift card.
 *
 * UWAGA dla admina sklepu:
 *   Nie twórz kodów rabatowych z 16+ ciągłymi znakami alfanumerycznymi,
 *   bo zostaną błędnie rozpoznane jako gift card. Bezpiecznie: używaj
 *   myślników/podkreśleń (np. WELCOME-2026) lub krótszych nazw.
 *
 * V6: dodana obsługa gift cards (sessionStorage + URL injection do checkout).
 */

(function () {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const GIFT_CARD_STORAGE_KEY = 'sw_gift_cards';
  const GIFT_CARD_MIN_LENGTH = 16;

  // ---------- Helpers: gift card detection & masking ----------

  /**
   * Strip wszystkich separatorów (myślniki, spacje, podkreślenia) i upper-case.
   */
  function normalizeCode(raw) {
    return String(raw || '').replace(/[\s\-_]+/g, '').toUpperCase();
  }

  /**
   * Czy ciąg wygląda jak gift card?
   * Kryterium: po normalizacji 16+ znaków, wyłącznie A-Z i 0-9.
   */
  function looksLikeGiftCard(raw) {
    const norm = normalizeCode(raw);
    if (norm.length < GIFT_CARD_MIN_LENGTH) return false;
    return /^[A-Z0-9]+$/.test(norm);
  }

  /**
   * Zwraca zamaskowaną postać numeru gift card do wyświetlenia w chipie.
   * Pokazuje pierwsze 4 i ostatnie 4 znaki, środek jako •••.
   * Przykład: "ABCD-EFGH-IJKL-MNOP" → "ABCD••••MNOP"
   */
  function maskGiftCard(code) {
    const norm = normalizeCode(code);
    if (norm.length <= 8) return norm;
    return `${norm.slice(0, 4)}••••${norm.slice(-4)}`;
  }

  // ---------- Gift card storage (sessionStorage) ----------

  function getStoredGiftCards() {
    try {
      const raw = sessionStorage.getItem(GIFT_CARD_STORAGE_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.filter(c => typeof c === 'string') : [];
    } catch {
      return [];
    }
  }

  function setStoredGiftCards(cards) {
    try {
      sessionStorage.setItem(GIFT_CARD_STORAGE_KEY, JSON.stringify(cards));
    } catch (err) {
      console.warn('[swederm-discount] sessionStorage write failed', err);
    }
  }

  function addStoredGiftCard(code) {
    const norm = normalizeCode(code);
    const existing = getStoredGiftCards();
    if (existing.some(c => normalizeCode(c) === norm)) return;
    existing.push(norm);
    setStoredGiftCards(existing);
  }

  function removeStoredGiftCard(code) {
    const norm = normalizeCode(code);
    const existing = getStoredGiftCards().filter(c => normalizeCode(c) !== norm);
    setStoredGiftCards(existing);
  }

  // ---------- Cart API (discount codes) ----------

  const CartAPI = {
    async get() {
      const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      return r.json();
    },
    async update(payload) {
      const r = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw await r.json();
      return r.json();
    },
  };

  function getAllActiveDiscounts(cart) {
    const codes = new Set();
    (cart.cart_level_discount_applications || []).forEach(d => {
      const code = d.title || d.code;
      if (code) codes.add(code);
    });
    (cart.items || []).forEach(item => {
      (item.line_level_discount_allocations || []).forEach(alloc => {
        const code = alloc.discount_application?.title;
        if (code) codes.add(code);
      });
      (item.discount_allocations || []).forEach(alloc => {
        const code = alloc.discount_application?.title;
        if (code) codes.add(code);
      });
    });
    return Array.from(codes);
  }

  // ---------- Drawer refresh ----------

  function notifyCartRefresh() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  async function refreshDrawerSection() {
    try {
      const url = `${window.location.pathname}?sections=cart-drawer`;
      const r = await fetch(url);
      const data = await r.json();
      const html = data['cart-drawer'];
      if (!html) return;

      const drawer = $('#Cart-Drawer');
      if (!drawer) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newInner = doc.querySelector('#Cart-Drawer .side-panel-inner');
      const oldInner = $('.side-panel-inner', drawer);
      if (newInner && oldInner) {
        oldInner.innerHTML = newInner.innerHTML;
      }
    } catch (err) {
      console.error('[swederm-discount] refresh drawer', err);
    }
  }

  async function fullRefresh() {
    notifyCartRefresh();
    setTimeout(async () => {
      await refreshDrawerSection();
      renderGiftCardChips(); // domalowujemy chipy gift card po każdym refreshu
    }, 100);
  }

  // ---------- Gift card chips rendering ----------

  /**
   * Liquid render nie zna sessionStorage, więc chipy gift card dorysowujemy
   * po stronie JS. Wkładamy je do tej samej listy <ul data-sw-discount-chips>,
   * obok chipów discount renderowanych przez Liquid.
   */
  function renderGiftCardChips() {
    const block = $('[data-sw-discount]');
    if (!block) return;
    const list = $('[data-sw-discount-chips]', block);
    if (!list) return;

    // Usuń stare chipy gift card (te dodane przez JS — oznaczone atrybutem)
    $$('[data-sw-giftcard-chip]', list).forEach(el => el.remove());

    const cards = getStoredGiftCards();
    if (cards.length === 0) {
      // Jeśli lista była ukryta przez Liquid (hidden), a teraz mamy gift cards
      // — odsłaniamy ją tylko jeśli są chipy. Tu nic do roboty.
      return;
    }

    // Lista mogła być z atrybutem hidden (gdy w koszyku nie było rabatów)
    list.removeAttribute('hidden');

    cards.forEach(code => {
      const li = document.createElement('li');
      li.setAttribute('data-sw-giftcard-chip', '');
      li.innerHTML = `
        <span class="sw-chip">
          <span class="sw-chip__dot" aria-hidden="true"></span>
          <span class="sw-chip__label">Karta podarunkowa: ${escapeHtml(maskGiftCard(code))}</span>
          <button type="button"
                  class="sw-chip__remove"
                  data-sw-giftcard-remove="${escapeAttr(code)}"
                  aria-label="Usuń kartę podarunkową">×</button>
        </span>
      `;
      list.appendChild(li);
    });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }
  function escapeAttr(s) { return escapeHtml(s); }

  // ---------- Apply / remove handlers ----------

  async function applyDiscount(code) {
    if (!code) return;
    const block = $('[data-sw-discount]');
    if (!block) return;
    const errEl = $('[data-sw-discount-error]', block);
    const input = $('[data-sw-discount-input]', block);
    const applyBtn = $('[data-sw-discount-apply]', block);

    if (errEl) errEl.hidden = true;
    if (applyBtn) applyBtn.disabled = true;

    try {
      // GAŁĄŹ A: gift card → sessionStorage + chip, BEZ /cart/update.js
      if (looksLikeGiftCard(code)) {
        addStoredGiftCard(code);
        if (input) input.value = '';
        renderGiftCardChips();
        return; // ważne: nie idziemy w gałąź discount
      }

      // GAŁĄŹ B: discount code (jak dotychczas)
      const cart = await CartAPI.get();
      const existing = getAllActiveDiscounts(cart);

      const codeUpper = code.toUpperCase();
      const alreadyApplied = existing.some((c) => c.toUpperCase() === codeUpper);
      const combined = alreadyApplied
        ? existing.join(',')
        : [...existing, code].join(',');

      await CartAPI.update({ discount: combined });

      const updated = await CartAPI.get();
      const appliedNow = getAllActiveDiscounts(updated);
      const ok = appliedNow.some(c => c.toUpperCase() === codeUpper);

      if (!ok) {
        if (errEl) {
          errEl.textContent = 'Kod nieprawidłowy, wygasł lub nie spełnia warunków zamówienia.';
          errEl.hidden = false;
        }
        // Koszyk się nie zmienił (kod nie wszedł). Pomijamy fullRefresh, bo
        // re-render sekcji podmieniłby .sw-discount__error i komunikat by zniknął.
        return;
      }

      if (input) input.value = '';
      await fullRefresh();
    } catch (err) {
      console.error('[swederm-discount] applyDiscount', err);
      if (errEl) {
        errEl.textContent = 'Nie udało się zastosować kodu. Spróbuj ponownie.';
        errEl.hidden = false;
      }
    } finally {
      if (applyBtn) applyBtn.disabled = false;
    }
  }

  async function removeDiscount(codeToRemove) {
    try {
      const cart = await CartAPI.get();
      const remaining = getAllActiveDiscounts(cart).filter(c => c !== codeToRemove);
      await CartAPI.update({ discount: remaining.join(',') });
      await fullRefresh();
    } catch (err) {
      console.error('[swederm-discount] removeDiscount', err);
    }
  }

  /**
   * Usunięcie karty podarunkowej.
   *
   * UWAGA na quirk Shopify: jeśli użytkownik wcześniej już odwiedził
   * /checkout z tą kartą (URL ?discount=...), Shopify zapamiętuje ją
   * w sesji checkoutu po stronie serwera. Sam fakt usunięcia chipa
   * (czyszczenie sessionStorage) NIE usuwa karty z tej sesji.
   *
   * Próbujemy dwóch rzeczy, żeby zminimalizować problem:
   *   1) Dummy update koszyka (touch cart note) — czasem wymusza
   *      odświeżenie cart tokena i sesji checkoutu. Brak gwarancji.
   *   2) Pokazujemy użytkownikowi komunikat, że jeśli już był w kasie
   *      z tą kartą, musi ją tam też usunąć ręcznie.
   *
   * Definitywne rozwiązanie wymagałoby Storefront API GraphQL
   * (cartGiftCardCodesRemove, od API 2025-01) i migracji koszyka
   * — to osobny, większy projekt.
   */
  async function removeGiftCard(code) {
    removeStoredGiftCard(code);
    renderGiftCardChips();

    // Próba unstucknięcia sesji checkoutu — touch cart note
    try {
      const cart = await CartAPI.get();
      await CartAPI.update({ note: cart.note || '' });
    } catch (err) {
      // Nie blokujemy UX jeśli się nie uda — to best-effort
      console.warn('[swederm-discount] cart touch after gift card remove failed', err);
    }

    // Komunikat informacyjny — pokazujemy w polu błędu, ale z innym stylem
    const block = $('[data-sw-discount]');
    const errEl = block ? $('[data-sw-discount-error]', block) : null;
    if (errEl) {
      errEl.textContent = 'Karta usunięta. Jeśli była już dodana w kasie, usuń ją również w polu „Kod rabatowy lub karta podarunkowa” w kasie.';
      errEl.hidden = false;
      // Po 8 sekundach chowamy
      setTimeout(() => {
        if (errEl.textContent.startsWith('Karta usunięta')) {
          errEl.hidden = true;
          errEl.textContent = '';
        }
      }, 8000);
    }
  }

  // ---------- Checkout button: inject gift cards into URL ----------

  /**
   * Shopify checkout konsumuje gift cards i discounts z tego samego paramu
   * ?discount=CODE w URL (można też podać wiele oddzielonych przecinkiem,
   * choć Shopify oficjalnie wspiera jeden — wiele jako lista CSV jest
   * "best-effort"). My przekazujemy tylko gift cards; discount codes są
   * już w cart obiekcie po stronie serwera.
   *
   * Hookujemy się w submit formularza /cart, podmieniamy action na
   * /checkout?discount=... — Shopify obsługuje to natywnie.
   */
  function bindCheckoutInjection() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('button[name="checkout"]');
      if (!btn) return;

      const cards = getStoredGiftCards();
      if (cards.length === 0) return; // nic do dorzucenia

      const form = btn.closest('form[action^="/cart"], form[action*="/cart"]');
      if (!form) return;

      // Sklejamy wszystkie gift cards CSV-em.
      // (Shopify checkout potrafi przyjąć wiele oddzielonych przecinkami.)
      const discountParam = cards.map(c => normalizeCode(c)).join(',');

      // Zamiast modyfikować action (które normalnie wskazuje /cart i
      // Shopify sam przekierowuje do /checkout), używamy hidden inputa
      // o nazwie "checkout" — to standardowy mechanizm Shopify cart formu —
      // i dorzucamy URL parameter przez nawigację.
      //
      // Najczystsze: ustaw action na /checkout?discount=... i pozwól
      // formowi go submitować. Shopify zaakceptuje.
      try {
        const u = new URL(form.action, window.location.origin);
        u.pathname = '/checkout';
        u.searchParams.set('discount', discountParam);
        form.action = u.pathname + u.search;
      } catch (err) {
        console.warn('[swederm-discount] checkout URL inject failed', err);
      }
    }, true); // capture: chwytamy zanim theme zrobi własną nawigację
  }

  // ---------- Global event listeners ----------

  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!t) return;

    if (t.closest('[data-sw-discount-apply]')) {
      e.preventDefault();
      const input = $('[data-sw-discount-input]');
      if (input) applyDiscount(input.value.trim());
      return;
    }

    const chipRemove = t.closest('[data-sw-discount-remove]');
    if (chipRemove) {
      e.preventDefault();
      const code = chipRemove.dataset.swDiscountRemove;
      if (code) removeDiscount(code);
      return;
    }

    const giftCardRemove = t.closest('[data-sw-giftcard-remove]');
    if (giftCardRemove) {
      e.preventDefault();
      const code = giftCardRemove.dataset.swGiftcardRemove;
      if (code) removeGiftCard(code);
      return;
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.target.matches('[data-sw-discount-input]') && e.key === 'Enter') {
      e.preventDefault();
      applyDiscount(e.target.value.trim());
    }
  });

  // Po każdym otwarciu drawera / refresh — odrysuj chipy gift card
  document.addEventListener('cart:refresh', () => {
    setTimeout(renderGiftCardChips, 150);
  });

  // Init
  bindCheckoutInjection();
  document.addEventListener('DOMContentLoaded', renderGiftCardChips);
  if (document.readyState !== 'loading') renderGiftCardChips();
})();
